import { useQuery, useMutation, useQueryClient } from 'react-query'
import React, { useState } from 'react'
import { 
  Route, 
  Users, 
  UserCheck, 
  Calendar, 
  Truck, 
  TrendingUp,
  TrendingDown,
  MapPin,
  DollarSign,
  Plus,
  Settings,
  Download,
  Eye,
  MoreVertical,
  Edit,
  Trash2,
  Send
} from 'lucide-react'
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts'
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Button,
  Badge,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Center,
  SimpleGrid,
  Input,
  Avatar,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Textarea,
  Select,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  useToast,
  Tag,
  TagLabel,
  TagCloseButton,
  Spinner,
} from '@chakra-ui/react'
import { cmsService } from '@services/api'
import { useAuthStore } from '@store/authStore'

interface StatCardProps {
  title: string
  value: string | number
  change?: string
  icon: React.ComponentType<{ className?: string }>
  color: string
}

interface RouteFormData {
  routeName: string
  routeNumber: string
  startPoint: string
  endPoint: string
  estimatedTimeMinutes: number
  description: string
  status: string
}

interface RouteData {
  id: number
  name: string
  routeCode: string
  startPoint: string
  endPoint: string
  estimatedDurationMinutes?: number
  active: boolean
  createdAt: string
  updatedAt: string
}

// Chart colors
const COLORS = ['#22C55E', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6']

// Filter options
const timeRanges = [
  { value: '7d', label: 'Last 7 Days' },
  { value: '30d', label: 'Last 30 Days' },
  { value: '3m', label: 'Last 3 Months' },
  { value: '6m', label: 'Last 6 Months' },
  { value: '1y', label: 'Last Year' }
]

const routeOptions = [
  { value: 'all', label: 'All Routes' },
  { value: 'route-a', label: 'Route A' },
  { value: 'route-b', label: 'Route B' },
  { value: 'route-c', label: 'Route C' },
  { value: 'route-d', label: 'Route D' },
  { value: 'route-e', label: 'Route E' }
]

function StatCard({ title, value, change, icon: Icon, color }: StatCardProps) {
  const iconBg = color === 'bg-primary-500' ? 'brand.500' : 
                 color === 'bg-success-500' ? 'green.500' :
                 color === 'bg-accent-500' ? 'orange.500' :
                 color === 'bg-warning-500' ? 'yellow.500' :
                 color === 'bg-secondary-500' ? 'purple.500' : 'brand.600'

  return (
    <Card variant="glass" _hover={{ transform: 'translateY(-2px)', shadow: 'xl' }} transition="all 0.3s">
      <CardBody p={6}>
        <Flex align="center" justify="space-between">
          <VStack align="start" spacing={2}>
            <Text fontSize="sm" fontWeight="medium" color="gray.600" opacity={0.8}>
              {title}
            </Text>
            <Text fontSize="3xl" fontWeight="bold" color="gray.900">
              {value}
            </Text>
          {change && (
              <HStack spacing={1}>
                {change.startsWith('+') ? (
                  <TrendingUp size={16} color="var(--chakra-colors-green-500)" />
                ) : (
                  <TrendingDown size={16} color="var(--chakra-colors-red-500)" />
                )}
                <Text fontSize="sm" fontWeight="medium" color={change.startsWith('+') ? 'green.500' : 'red.500'}>
              {change} from last month
                </Text>
              </HStack>
            )}
          </VStack>
          <Box 
            p={4} 
            bg={`linear-gradient(135deg, ${iconBg}, ${iconBg}CC)`}
            borderRadius="2xl"
            boxShadow="lg"
          >
            <Icon className="w-7 h-7 text-white" />
          </Box>
        </Flex>
      </CardBody>
    </Card>
  )
}

function RouteFormModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [formData, setFormData] = useState<RouteFormData>({
    routeName: '',
    routeNumber: '',
    startPoint: '',
    endPoint: '',
    estimatedTimeMinutes: 30,
    description: '',
    status: 'active'
  })
  const [isLoading, setIsLoading] = useState(false)
  const toast = useToast()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      toast({
        title: 'Route Created',
        description: `${formData.routeName} has been successfully created.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
      
      onClose()
      setFormData({
        routeName: '',
        routeNumber: '',
        startPoint: '',
        endPoint: '',
        estimatedTimeMinutes: 30,
        description: '',
        status: 'active'
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to create route. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: 'xl' }}>
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(10px)" />
      <ModalContent borderRadius={{ base: 'none', md: '2xl' }} m={{ base: 0, md: 4 }} maxH={{ base: '100vh', md: '90vh' }}>
        <ModalHeader>
          <HStack spacing={3}>
            <Box p={2} bg="brand.100" borderRadius="lg">
              <Route size={20} color="var(--chakra-colors-brand-600)" />
            </Box>
            <Text fontSize="xl" fontWeight="bold">Add New Route</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <form onSubmit={handleSubmit}>
            <VStack spacing={4}>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="full">
                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="medium">Route Name</FormLabel>
                  <Input
                    value={formData.routeName}
                    onChange={(e) => setFormData({...formData, routeName: e.target.value})}
                    placeholder="e.g., Downtown Express"
                    borderRadius="xl"
                    borderColor="gray.300"
                    _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                  />
                </FormControl>
                
                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="medium">Route Number</FormLabel>
                  <Input
                    value={formData.routeNumber}
                    onChange={(e) => setFormData({...formData, routeNumber: e.target.value})}
                    placeholder="e.g., R001"
                    borderRadius="xl"
                    borderColor="gray.300"
                    _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                  />
                </FormControl>
              </SimpleGrid>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="full">
                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="medium">Start Point</FormLabel>
                  <Input
                    value={formData.startPoint}
                    onChange={(e) => setFormData({...formData, startPoint: e.target.value})}
                    placeholder="e.g., City Center"
                    borderRadius="xl"
                    borderColor="gray.300"
                    _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                  />
                </FormControl>
                
                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="medium">End Point</FormLabel>
                  <Input
                    value={formData.endPoint}
                    onChange={(e) => setFormData({...formData, endPoint: e.target.value})}
                    placeholder="e.g., Airport Terminal"
                    borderRadius="xl"
                    borderColor="gray.300"
                    _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                  />
                </FormControl>
              </SimpleGrid>

              <SimpleGrid columns={2} spacing={4} w="full">
                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="medium">Estimated Time (minutes)</FormLabel>
                  <NumberInput
                    value={formData.estimatedTimeMinutes}
                    onChange={(_, value) => setFormData({...formData, estimatedTimeMinutes: value || 30})}
                    min={5}
                    max={300}
                  >
                    <NumberInputField borderRadius="xl" borderColor="gray.300" />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>
                
                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="medium">Status</FormLabel>
                  <Select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    borderRadius="xl"
                    borderColor="gray.300"
                    _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="maintenance">Maintenance</option>
                  </Select>
                </FormControl>
              </SimpleGrid>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium">Description</FormLabel>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Route description and additional information..."
                  borderRadius="xl"
                  borderColor="gray.300"
                  _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                  rows={3}
                />
              </FormControl>

              <HStack spacing={3} w="full" pt={4}>
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  flex={1}
                  borderRadius="xl"
                  borderColor="gray.300"
                  _hover={{ bg: 'gray.50' }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  colorScheme="brand"
                  flex={1}
                  borderRadius="xl"
                  isLoading={isLoading}
                  loadingText="Creating..."
                  leftIcon={<Plus size={16} />}
                >
                  Create Route
                </Button>
              </HStack>
            </VStack>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

function RouteViewModal({ isOpen, onClose, route }: { isOpen: boolean; onClose: () => void; route: RouteData | null }) {
  if (!route) return null

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: 'lg' }}>
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(10px)" />
      <ModalContent borderRadius={{ base: 'none', md: '2xl' }} m={{ base: 0, md: 4 }}>
        <ModalHeader>
          <HStack spacing={3}>
            <Box p={2} bg="brand.100" borderRadius="lg">
              <Route size={20} color="var(--chakra-colors-brand-600)" />
            </Box>
            <VStack align="start" spacing={0}>
              <Text fontSize="xl" fontWeight="bold">{route.name}</Text>
              <Text fontSize="sm" color="gray.600">Route Details</Text>
            </VStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <VStack spacing={6} align="stretch">
            <Card bg="gray.50" border="1px solid" borderColor="gray.200">
              <CardBody>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <VStack align="start" spacing={2}>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">Route Code</Text>
                    <Text fontSize="lg" fontWeight="semibold" color="gray.900">{route.routeCode}</Text>
                  </VStack>
                  <VStack align="start" spacing={2}>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">Status</Text>
                    <Badge
                      colorScheme={route.active ? 'green' : 'gray'}
                      variant="subtle"
                      borderRadius="full"
                      px={3}
                      py={1}
                    >
                      {route.active ? 'Active' : 'Inactive'}
                    </Badge>
                  </VStack>
                </SimpleGrid>
              </CardBody>
            </Card>

            <Card bg="gray.50" border="1px solid" borderColor="gray.200">
              <CardBody>
                <VStack spacing={4} align="stretch">
                  <HStack spacing={1} fontSize="md" color="gray.600">
                    <MapPin size={16} />
                    <Text fontWeight="medium">Route Path</Text>
                  </HStack>
                  <HStack spacing={4} align="center">
                    <VStack align="start" spacing={1} flex={1}>
                      <Text fontSize="sm" color="gray.600">Start Point</Text>
                      <Text fontSize="lg" fontWeight="semibold" color="gray.900">{route.startPoint}</Text>
                    </VStack>
                    <Box color="gray.400">
                      <Route size={20} />
                    </Box>
                    <VStack align="start" spacing={1} flex={1}>
                      <Text fontSize="sm" color="gray.600">End Point</Text>
                      <Text fontSize="lg" fontWeight="semibold" color="gray.900">{route.endPoint}</Text>
                    </VStack>
                  </HStack>
                </VStack>
              </CardBody>
            </Card>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <Card bg="gray.50" border="1px solid" borderColor="gray.200">
                <CardBody>
                  <VStack align="start" spacing={2}>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">Estimated Duration</Text>
                    <Text fontSize="lg" fontWeight="semibold" color="gray.900">{route.estimatedDurationMinutes || 'N/A'} minutes</Text>
                  </VStack>
                </CardBody>
              </Card>
              <Card bg="gray.50" border="1px solid" borderColor="gray.200">
                <CardBody>
                  <VStack align="start" spacing={2}>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">Created</Text>
                    <Text fontSize="lg" fontWeight="semibold" color="gray.900">
                      {new Date(route.createdAt).toLocaleDateString()}
                    </Text>
                  </VStack>
                </CardBody>
              </Card>
            </SimpleGrid>


            <HStack spacing={3} w="full" pt={4}>
              <Button variant="outline" onClick={onClose} flex={1} borderRadius="xl">
                Close
              </Button>
            </HStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

function RouteEditModal({ isOpen, onClose, route }: { isOpen: boolean; onClose: () => void; route: RouteData | null }) {
  const [formData, setFormData] = useState<RouteFormData>({
    routeName: '',
    routeNumber: '',
    startPoint: '',
    endPoint: '',
    estimatedTimeMinutes: 30,
    description: '',
    status: 'active'
  })
  const [isLoading, setIsLoading] = useState(false)
  const toast = useToast()
  const queryClient = useQueryClient()

  // Update form data when route changes
  React.useEffect(() => {
    if (route) {
      setFormData({
        routeName: route.name,
        routeNumber: route.routeCode,
        startPoint: route.startPoint,
        endPoint: route.endPoint,
        estimatedTimeMinutes: route.estimatedDurationMinutes || 30,
        description: '',
        status: route.active ? 'active' : 'inactive'
      })
    }
  }, [route])

  const updateRouteMutation = useMutation(
    (data: RouteFormData) => cmsService.updateRoute(route?.id || 0, {
      name: data.routeName,
      routeCode: data.routeNumber,
      startPoint: data.startPoint,
      endPoint: data.endPoint,
      estimatedDurationMinutes: data.estimatedTimeMinutes
    }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('routes')
        queryClient.invalidateQueries('dashboard-stats')
        toast({
          title: 'Route Updated',
          description: `${formData.routeName} has been successfully updated.`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
        onClose()
      },
      onError: (error: any) => {
        toast({
          title: 'Error',
          description: error?.response?.data?.message || 'Failed to update route. Please try again.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        })
      }
    }
  )

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    updateRouteMutation.mutate(formData)
    setIsLoading(false)
  }

  if (!route) return null

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: 'xl' }}>
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(10px)" />
      <ModalContent borderRadius={{ base: 'none', md: '2xl' }} m={{ base: 0, md: 4 }} maxH={{ base: '100vh', md: '90vh' }}>
        <ModalHeader>
          <HStack spacing={3}>
            <Box p={2} bg="brand.100" borderRadius="lg">
              <Edit size={20} color="var(--chakra-colors-brand-600)" />
            </Box>
            <Text fontSize="xl" fontWeight="bold">Edit Route</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <form onSubmit={handleSubmit}>
            <VStack spacing={4}>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="full">
                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="medium">Route Name</FormLabel>
                  <Input
                    value={formData.routeName}
                    onChange={(e) => setFormData({...formData, routeName: e.target.value})}
                    placeholder="e.g., Downtown Express"
                    borderRadius="xl"
                    borderColor="gray.300"
                    _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                  />
                </FormControl>
                
                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="medium">Route Number</FormLabel>
                  <Input
                    value={formData.routeNumber}
                    onChange={(e) => setFormData({...formData, routeNumber: e.target.value})}
                    placeholder="e.g., R001"
                    borderRadius="xl"
                    borderColor="gray.300"
                    _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                  />
                </FormControl>
              </SimpleGrid>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="full">
                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="medium">Start Point</FormLabel>
                  <Input
                    value={formData.startPoint}
                    onChange={(e) => setFormData({...formData, startPoint: e.target.value})}
                    placeholder="e.g., City Center"
                    borderRadius="xl"
                    borderColor="gray.300"
                    _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                  />
                </FormControl>
                
                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="medium">End Point</FormLabel>
                  <Input
                    value={formData.endPoint}
                    onChange={(e) => setFormData({...formData, endPoint: e.target.value})}
                    placeholder="e.g., Airport Terminal"
                    borderRadius="xl"
                    borderColor="gray.300"
                    _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                  />
                </FormControl>
              </SimpleGrid>

              <SimpleGrid columns={2} spacing={4} w="full">
                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="medium">Estimated Time (minutes)</FormLabel>
                  <NumberInput
                    value={formData.estimatedTimeMinutes}
                    onChange={(_, value) => setFormData({...formData, estimatedTimeMinutes: value || 30})}
                    min={5}
                    max={300}
                  >
                    <NumberInputField borderRadius="xl" borderColor="gray.300" />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>
                
                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="medium">Status</FormLabel>
                  <Select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    borderRadius="xl"
                    borderColor="gray.300"
                    _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="maintenance">Maintenance</option>
                  </Select>
                </FormControl>
              </SimpleGrid>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium">Description</FormLabel>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Route description and additional information..."
                  borderRadius="xl"
                  borderColor="gray.300"
                  _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                  rows={3}
                />
              </FormControl>

              <HStack spacing={3} w="full" pt={4}>
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  flex={1}
                  borderRadius="xl"
                  borderColor="gray.300"
                  _hover={{ bg: 'gray.50' }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  colorScheme="brand"
                  flex={1}
                  borderRadius="xl"
                  isLoading={isLoading}
                  loadingText="Updating..."
                  leftIcon={<Edit size={16} />}
                >
                  Update Route
                </Button>
              </HStack>
            </VStack>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

// Chart Components
function RevenueChart({ data, isLoading }: { data?: any; isLoading?: boolean }) {
  const [timeRange, setTimeRange] = useState('6m')
  const [selectedRoutes, setSelectedRoutes] = useState(['all'])

  const handleRouteToggle = (routeValue: string) => {
    if (routeValue === 'all') {
      setSelectedRoutes(['all'])
    } else {
      setSelectedRoutes(prev => {
        const filtered = prev.filter(r => r !== 'all')
        if (filtered.includes(routeValue)) {
          return filtered.filter(r => r !== routeValue)
        } else {
          return [...filtered, routeValue]
        }
      })
    }
  }

  // Use real data or fallback to empty array - ensure it's always an array
  const chartData = Array.isArray(data?.data) ? data.data : []

  return (
    <Card variant="glass" h="450px">
      <CardHeader>
        <Flex align="center" justify="space-between" w="full">
          <VStack align="start" spacing={1}>
            <Heading size="md" color="gray.900">Revenue Analytics</Heading>
            <Text fontSize="sm" color="gray.600">Monthly revenue comparison</Text>
          </VStack>
          
          <HStack spacing={3}>
            <Select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              size="sm"
              w="140px"
              borderRadius="xl"
              borderColor="gray.300"
            >
              {timeRanges.map(range => (
                <option key={range.value} value={range.value}>{range.label}</option>
              ))}
            </Select>
          </HStack>
        </Flex>
        
        {/* Route Filters */}
        <Flex wrap="wrap" gap={2} mt={3}>
          {routeOptions.map(route => (
            <Tag
              key={route.value}
              size="sm"
              variant={selectedRoutes.includes(route.value) ? "solid" : "outline"}
              colorScheme={selectedRoutes.includes(route.value) ? "brand" : "gray"}
              cursor="pointer"
              onClick={() => handleRouteToggle(route.value)}
              borderRadius="full"
            >
              <TagLabel>{route.label}</TagLabel>
              {selectedRoutes.includes(route.value) && route.value !== 'all' && (
                <TagCloseButton onClick={(e) => {
                  e.stopPropagation()
                  handleRouteToggle(route.value)
                }} />
              )}
            </Tag>
          ))}
        </Flex>
      </CardHeader>
      <CardBody pt={0}>
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="colorOnline" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#22C55E" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="#22C55E" stopOpacity={0.1}/>
              </linearGradient>
              <linearGradient id="colorOffline" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.1}/>
              </linearGradient>
            </defs>
            <XAxis dataKey="name" axisLine={false} tickLine={false} />
            <YAxis axisLine={false} tickLine={false} />
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <Tooltip 
              contentStyle={{
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                border: 'none',
                borderRadius: '12px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
              }}
            />
            <Legend />
            <Area type="monotone" dataKey="online" stroke="#22C55E" fillOpacity={1} fill="url(#colorOnline)" name="Online Revenue" />
            <Area type="monotone" dataKey="offline" stroke="#3B82F6" fillOpacity={1} fill="url(#colorOffline)" name="Offline Revenue" />
          </AreaChart>
        </ResponsiveContainer>
      </CardBody>
    </Card>
  )
}

function RoutePerformanceChart({ data, isLoading }: { data?: any; isLoading?: boolean }) {
  const [metric, setMetric] = useState('trips')
  const [timeRange, setTimeRange] = useState('30d')

  // Use real data or fallback to empty array - ensure it's always an array
  const chartData = Array.isArray(data?.data) ? data.data : []

  return (
    <Card variant="glass" h="450px">
      <CardHeader>
        <Flex align="center" justify="space-between" w="full">
          <VStack align="start" spacing={1}>
            <Heading size="md" color="gray.900">Route Performance</Heading>
            <Text fontSize="sm" color="gray.600">Top performing routes this month</Text>
          </VStack>
          
          <HStack spacing={3}>
            <Select
              value={metric}
              onChange={(e) => setMetric(e.target.value)}
              size="sm"
              w="120px"
              borderRadius="xl"
              borderColor="gray.300"
            >
              <option value="trips">Trips</option>
              <option value="revenue">Revenue</option>
              <option value="passengers">Passengers</option>
            </Select>
            <Select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              size="sm"
              w="140px"
              borderRadius="xl"
              borderColor="gray.300"
            >
              {timeRanges.map(range => (
                <option key={range.value} value={range.value}>{range.label}</option>
              ))}
            </Select>
          </HStack>
        </Flex>
      </CardHeader>
      <CardBody pt={0}>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis dataKey="name" axisLine={false} tickLine={false} />
            <YAxis axisLine={false} tickLine={false} />
            <Tooltip 
              contentStyle={{
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                border: 'none',
                borderRadius: '12px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
              }}
            />
            <Bar 
              dataKey={metric} 
              fill={metric === 'trips' ? '#22C55E' : metric === 'revenue' ? '#3B82F6' : '#F59E0B'} 
              radius={[4, 4, 0, 0]} 
              name={metric === 'trips' ? 'Number of Trips' : metric === 'revenue' ? 'Revenue (TZS)' : 'Passengers'} 
            />
          </BarChart>
        </ResponsiveContainer>
      </CardBody>
    </Card>
  )
}

function WeeklyBookingsChart({ data, isLoading }: { data?: any; isLoading?: boolean }) {
  const [period, setPeriod] = useState('week')
  const [view, setView] = useState('bookings')

  // Use real data or fallback to empty array - ensure it's always an array
  const chartData = Array.isArray(data?.data) ? data.data : []

  return (
    <Card variant="glass" h="350px">
      <CardHeader>
        <Flex align="center" justify="space-between" w="full">
          <VStack align="start" spacing={1}>
            <Heading size="md" color="gray.900">Booking Trends</Heading>
            <Text fontSize="sm" color="gray.600">This week's booking trends</Text>
          </VStack>
          
          <HStack spacing={3}>
            <Select
              value={view}
              onChange={(e) => setView(e.target.value)}
              size="sm"
              w="120px"
              borderRadius="xl"
              borderColor="gray.300"
            >
              <option value="bookings">Bookings</option>
              <option value="revenue">Revenue</option>
              <option value="passengers">Passengers</option>
            </Select>
            <Select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              size="sm"
              w="100px"
              borderRadius="xl"
              borderColor="gray.300"
            >
              <option value="week">Week</option>
              <option value="month">Month</option>
              <option value="quarter">Quarter</option>
            </Select>
          </HStack>
        </Flex>
      </CardHeader>
      <CardBody pt={0}>
        <ResponsiveContainer width="100%" height={230}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis dataKey="name" axisLine={false} tickLine={false} />
            <YAxis axisLine={false} tickLine={false} />
            <Tooltip 
              contentStyle={{
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                border: 'none',
                borderRadius: '12px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
              }}
            />
            <Line 
              type="monotone" 
              dataKey="bookings" 
              stroke={view === 'bookings' ? '#22C55E' : view === 'revenue' ? '#3B82F6' : '#F59E0B'} 
              strokeWidth={3}
              dot={{ fill: view === 'bookings' ? '#22C55E' : view === 'revenue' ? '#3B82F6' : '#F59E0B', strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, stroke: view === 'bookings' ? '#22C55E' : view === 'revenue' ? '#3B82F6' : '#F59E0B', strokeWidth: 2 }}
              name={view === 'bookings' ? 'Bookings' : view === 'revenue' ? 'Revenue (TZS)' : 'Passengers'}
            />
          </LineChart>
        </ResponsiveContainer>
      </CardBody>
    </Card>
  )
}

function PaymentMethodsChart({ data, isLoading }: { data?: any; isLoading?: boolean }) {
  const [timeRange, setTimeRange] = useState('30d')
  const [selectedRoutes, setSelectedRoutes] = useState(['all'])

  const handleRouteToggle = (routeValue: string) => {
    if (routeValue === 'all') {
      setSelectedRoutes(['all'])
    } else {
      setSelectedRoutes(prev => {
        const filtered = prev.filter(r => r !== 'all')
        if (filtered.includes(routeValue)) {
          return filtered.filter(r => r !== routeValue)
        } else {
          return [...filtered, routeValue]
        }
      })
    }
  }

  // Use real data or fallback to empty array - ensure it's always an array
  const chartData = Array.isArray(data?.data) ? data.data : []

  return (
    <Card variant="glass" h="350px">
      <CardHeader>
        <Flex align="center" justify="space-between" w="full">
          <VStack align="start" spacing={1}>
            <Heading size="md" color="gray.900">Payment Methods</Heading>
            <Text fontSize="sm" color="gray.600">Revenue breakdown by payment type</Text>
          </VStack>
          
          <HStack spacing={3}>
            <Select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              size="sm"
              w="140px"
              borderRadius="xl"
              borderColor="gray.300"
            >
              {timeRanges.map(range => (
                <option key={range.value} value={range.value}>{range.label}</option>
              ))}
            </Select>
          </HStack>
        </Flex>
        
        {/* Route Filters */}
        <Flex wrap="wrap" gap={2} mt={3}>
          {routeOptions.slice(0, 4).map(route => (
            <Tag
              key={route.value}
              size="sm"
              variant={selectedRoutes.includes(route.value) ? "solid" : "outline"}
              colorScheme={selectedRoutes.includes(route.value) ? "brand" : "gray"}
              cursor="pointer"
              onClick={() => handleRouteToggle(route.value)}
              borderRadius="full"
            >
              <TagLabel>{route.label}</TagLabel>
              {selectedRoutes.includes(route.value) && route.value !== 'all' && (
                <TagCloseButton onClick={(e) => {
                  e.stopPropagation()
                  handleRouteToggle(route.value)
                }} />
              )}
            </Tag>
          ))}
        </Flex>
      </CardHeader>
      <CardBody pt={0}>
        <ResponsiveContainer width="100%" height={230}>
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={40}
              outerRadius={80}
              paddingAngle={2}
              dataKey="value"
            >
              {chartData.map((entry: any, index: number) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip 
              contentStyle={{
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                border: 'none',
                borderRadius: '12px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
              }}
            />
            <Legend 
              verticalAlign="bottom" 
              height={36}
              formatter={(value, entry) => (
                <span style={{ color: entry.color, fontSize: '14px' }}>
                  {value}
                </span>
              )}
            />
          </PieChart>
        </ResponsiveContainer>
      </CardBody>
    </Card>
  )
}

export default function Dashboard() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const { user } = useAuthStore()

  const displayName = user?.role === 'admin' 
    ? 'Admin'
    : (user?.firstName 
        ? `${user.firstName}${user.lastName ? ` ${user.lastName}` : ''}`
        : user?.email || user?.phoneNumber || 'Operator');

  // Broadcast Modal state
  const { isOpen: isBroadcastOpen, onOpen: onBroadcastOpen, onClose: onBroadcastClose } = useDisclosure();
  const [broadcastTitle, setBroadcastTitle] = useState('');
  const [broadcastBody, setBroadcastBody] = useState('');
  const [broadcastRole, setBroadcastRole] = useState('all_users');
  const [isBroadcasting, setIsBroadcasting] = useState(false);

  const handleSendBroadcast = async () => {
    if (!broadcastTitle || !broadcastBody) {
      toast({ title: 'Error', description: 'Title and Message are required', status: 'error' });
      return;
    }
    try {
      setIsBroadcasting(true);
      await cmsService.sendBroadcastNotification({
        title: broadcastTitle,
        body: broadcastBody,
        targetRole: broadcastRole === 'all_users' ? undefined : broadcastRole
      });
      toast({ title: 'Success', description: 'Broadcast sent successfully', status: 'success' });
      onBroadcastClose();
      setBroadcastTitle('');
      setBroadcastBody('');
    } catch (e: any) {
      toast({ title: 'Error', description: e.message || 'Failed to send broadcast', status: 'error' });
    } finally {
      setIsBroadcasting(false);
    }
  };

  // Get CMS dashboard statistics
  const { data: dashboardStats, isLoading: statsLoading } = useQuery(
    'dashboard-stats',
    () => cmsService.getDashboardStats(),
    {
      retry: false,
    }
  )

  const { data: routes, isLoading: routesLoading } = useQuery(
    'routes',
    () => cmsService.getRoutes({ limit: 5 }),
    {
      retry: false,
    }
  )

  const { data: vehicles, isLoading: vehiclesLoading } = useQuery(
    'vehicles',
    () => cmsService.getVehicles({ limit: 5 }),
    {
      retry: false,
    }
  )

  const { data: health, isLoading: healthLoading } = useQuery(
    'health',
    () => cmsService.healthCheck(),
    {
      retry: false,
    }
  )

  // Fetch real chart data
  const { data: revenueChartData, isLoading: revenueLoading } = useQuery(
    'dashboard-revenue',
    () => cmsService.getRevenueData({ timeRange: '6m' }),
    {
      retry: false,
    }
  )

  const { data: bookingsChartData, isLoading: bookingsLoading } = useQuery(
    'dashboard-bookings',
    () => cmsService.getBookingsData({ timeRange: '7d' }),
    {
      retry: false,
    }
  )

  const { data: routePerformanceData, isLoading: routePerformanceLoading } = useQuery(
    'dashboard-route-performance',
    () => cmsService.getRoutePerformance({ timeRange: '30d' }),
    {
      retry: false,
    }
  )

  const { data: paymentBreakdownData, isLoading: paymentBreakdownLoading } = useQuery(
    'dashboard-payment-breakdown',
    () => cmsService.getPaymentBreakdown({ timeRange: '30d' }),
    {
      retry: false,
    }
  )

  const { isOpen, onOpen, onClose } = useDisclosure()
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure()
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure()
  const [selectedRoute, setSelectedRoute] = useState<RouteData | null>(null)

  const handleViewRoute = (route: RouteData) => {
    setSelectedRoute(route)
    onViewOpen()
  }

  const handleEditRoute = (route: RouteData) => {
    setSelectedRoute(route)
    onEditOpen()
  }

  const handleDeleteRoute = async (route: RouteData) => {
    if (!window.confirm(`Delete route "${route.name}"? This action cannot be undone.`)) return;
    try {
      await cmsService.deleteRoute(route.id);
      toast({ title: 'Route deleted', status: 'success', duration: 3000, isClosable: true });
      queryClient.invalidateQueries({ queryKey: ['routes'] });
    } catch (error: any) {
      toast({ title: 'Failed to delete route', description: error?.message ?? 'Server error', status: 'error', duration: 5000, isClosable: true });
    }
  }

  // Real data from CMS
  const stats = [
    {
      title: 'Total Daladalas',
      value: dashboardStats?.data?.vehicles?.total || 0,
      change: dashboardStats?.data?.vehicles?.change || '0%',
      icon: Truck,
      color: 'bg-brand-500',
    },
    {
      title: 'Active Daladalas',
      value: dashboardStats?.data?.vehicles?.active || 0,
      change: dashboardStats?.data?.vehicles?.change || '0%',
      icon: UserCheck,
      color: 'bg-green-500',
    },
    {
      title: 'Available Drivers',
      value: dashboardStats?.data?.drivers?.active || 0,
      change: dashboardStats?.data?.drivers?.change || '0%',
      icon: Users,
      color: 'bg-blue-500',
    },
    {
      title: 'Total Routes',
      value: dashboardStats?.data?.routes?.total || 0,
      change: dashboardStats?.data?.routes?.change || '0%',
      icon: Route,
      color: 'bg-orange-500',
    },
    {
      title: 'With Drivers',
      value: dashboardStats?.data?.vehicles?.withDrivers || 0,
      change: dashboardStats?.data?.vehicles?.change || '0%',
      icon: Calendar,
      color: 'bg-purple-500',
    },
    {
      title: 'Available Conductors',
      value: dashboardStats?.data?.vehicles?.withConductors || 0,
      change: dashboardStats?.data?.vehicles?.change || '0%',
      icon: DollarSign,
      color: 'bg-brand-600',
    },
  ]

  if (statsLoading || routesLoading || vehiclesLoading || healthLoading) {
    return (
      <Center h="64">
        <Spinner size="lg" color="brand.500" />
      </Center>
    )
  }

  return (
    <Box minH="100vh" bgGradient="linear(to-br, brand.50, white, brand.100)">
      <VStack spacing={{ base: 6, md: 8 }} align="stretch" p={{ base: 4, md: 6 }}>
        {/* Modern Header */}
        <Card variant="glass" p={{ base: 4, md: 6 }}>
          <Flex align="center" justify="space-between" flexDirection={{ base: 'column', md: 'row' }} gap={4}>
            <VStack align={{ base: 'center', md: 'start' }} spacing={2} textAlign={{ base: 'center', md: 'left' }}>
              <HStack spacing={3}>
                <Heading size={{ base: 'lg', md: 'xl' }} color="gray.900" fontWeight="bold">
                  Welcome Back {displayName}!
                </Heading>
                {/* <Badge colorScheme="brand" variant="subtle" px={3} py={1} borderRadius="full">
                  Live
                </Badge> */}
              </HStack>
              <Text color="gray.600" fontSize={{ base: 'md', md: 'lg' }}>
                Daladala Smart statistics
              </Text>
            </VStack>
            
            <HStack spacing={4} w={{ base: 'full', md: 'auto' }} justify={{ base: 'center', md: 'flex-end' }}>
              <HStack spacing={2} fontSize="sm" color="gray.600" bg="brand.50" px={4} py={2} borderRadius="full">
                {/* <Box w="2" h="2" bg="brand.500" borderRadius="full" /> */}
                {/* <Text fontWeight="medium">System Online</Text> */}
              </HStack>
              {user?.role === 'admin' && (
                <Button 
                  leftIcon={<Send size={16} />} 
                  colorScheme="brand" 
                  borderRadius="xl"
                  size={{ base: 'sm', md: 'md' }}
                  onClick={onBroadcastOpen}
                >
                  Send Broadcast
                </Button>
              )}
              <Button 
                leftIcon={<Download size={16} />} 
                variant="outline" 
                borderRadius="xl"
                display={{ base: 'none', lg: 'flex' }}
                size={{ base: 'sm', md: 'md' }}
              >
                Download Data
              </Button>
            </HStack>
          </Flex>
        </Card>

      {/* Stats Grid */}
        <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={4} minChildWidth="200px">
        {stats.map((stat, index) => (
          <StatCard key={index} {...stat} />
        ))}
        </SimpleGrid>

        {/* Charts Section */}
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8}>
          <RevenueChart data={revenueChartData} isLoading={revenueLoading} />
          <RoutePerformanceChart data={routePerformanceData} isLoading={routePerformanceLoading} />
        </SimpleGrid>

        {/* Secondary Charts */}
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
          <WeeklyBookingsChart data={bookingsChartData} isLoading={bookingsLoading} />
          <PaymentMethodsChart data={paymentBreakdownData} isLoading={paymentBreakdownLoading} />
        </SimpleGrid>

        {/* Main Content Grid */}
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8}>
      {/* Recent Routes */}
          <Card variant="glass">
            <CardHeader>
              <Flex align="center" justify="space-between">
                <HStack spacing={3}>
                  <Box p={2} bg="brand.100" borderRadius="lg">
                    <Route size={20} color="var(--chakra-colors-brand-600)" />
                  </Box>
                  <VStack align="start" spacing={0}>
                    <Heading size="md" color="gray.900">
                      Recent Routes
                    </Heading>
                    <Text fontSize="sm" color="gray.600">
                      Latest route activities
                    </Text>
                  </VStack>
                </HStack>
                <Button variant="link" colorScheme="brand" size="sm" rightIcon={<Eye size={16} />}>
                  View More
                </Button>
              </Flex>
            </CardHeader>
            <CardBody>
          {routes?.data?.length ? (
                <VStack spacing={4} align="stretch">
              {routes.data.map((route) => (
                    <Flex
                      key={route.id}
                      align="center"
                      justify="space-between"
                      p={4}
                      bg="rgba(255, 255, 255, 0.5)"
                      borderRadius="xl"
                      border="1px solid"
                      borderColor="rgba(255, 255, 255, 0.2)"
                      _hover={{ bg: "rgba(255, 255, 255, 0.7)" }}
                      transition="all 0.3s"
                    >
                      <HStack spacing={4}>
                        <Avatar
                          size="md"
                          bg="brand.100"
                          icon={<Route size={20} color="var(--chakra-colors-brand-600)" />}
                        />
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="semibold" color="gray.900">
                            {route.name}
                          </Text>
                          <Text fontSize="sm" color="gray.600">
                            {route.routeCode} • {route.estimatedDurationMinutes}min
                          </Text>
                        </VStack>
                      </HStack>
                      <HStack spacing={4}>
                        <VStack spacing={1} align="end">
                          <HStack spacing={1} fontSize="sm" color="gray.600">
                            <MapPin size={14} />
                            <Text>{route.startPoint} → {route.endPoint}</Text>
                          </HStack>
                          <Badge
                            colorScheme={
                              route.active ? 'green' : 'gray'
                            }
                            variant="subtle"
                            borderRadius="full"
                          >
                            {route.active ? 'Active' : 'Inactive'}
                          </Badge>
                        </VStack>
                        <Menu>
                          <MenuButton
                            as={IconButton}
                            icon={<MoreVertical size={16} />}
                            variant="ghost"
                            size="sm"
                          />
                          <MenuList>
                            <MenuItem onClick={() => handleViewRoute(route)}>
                              <HStack spacing={2}>
                                <Eye size={16} />
                                <Text>View Details</Text>
                              </HStack>
                            </MenuItem>
                            <MenuItem onClick={() => handleEditRoute(route)}>
                              <HStack spacing={2}>
                                <Edit size={16} />
                                <Text>Edit Route</Text>
                              </HStack>
                            </MenuItem>
                            <MenuItem color="red.500" onClick={() => handleDeleteRoute(route)}>
                              <HStack spacing={2}>
                                <Trash2 size={16} />
                                <Text>Delete</Text>
                              </HStack>
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </HStack>
                    </Flex>
                  ))}
                </VStack>
              ) : (
                <Center py={12}>
                  <VStack spacing={6}>
                    <Box p={4} bg="brand.100" borderRadius="full">
                      <Route size={48} color="var(--chakra-colors-brand-500)" />
                    </Box>
                    <VStack spacing={2}>
                      <Heading size="md" color="gray.900">
                        No routes found
                      </Heading>
                      <Text color="gray.600" textAlign="center">
                        Get started by creating your first route to begin managing your transportation network.
                      </Text>
                    </VStack>
                    <Button 
                      colorScheme="brand" 
                      size="lg"
                      leftIcon={<Plus size={20} />}
                      onClick={onOpen}
                      borderRadius="xl"
                    >
                      Create Your First Route
                    </Button>
                  </VStack>
                </Center>
              )}
            </CardBody>
          </Card>

      {/* Quick Actions */}
          <Card variant="glass">
            <CardHeader>
              <HStack spacing={3}>
                <Box p={2} bg="purple.100" borderRadius="lg">
                  <Settings size={20} color="var(--chakra-colors-purple-600)" />
                </Box>
                <VStack align="start" spacing={0}>
                  <Heading size="md" color="gray.900">
                    Quick Actions
                  </Heading>
                  <Text fontSize="sm" color="gray.600">
                    Manage your system efficiently
                  </Text>
                </VStack>
              </HStack>
            </CardHeader>
            <CardBody>
              <SimpleGrid columns={1} spacing={4}>
                <Card
                  bg="rgba(255, 255, 255, 0.5)"
                  border="1px solid"
                  borderColor="rgba(255, 255, 255, 0.2)"
                  _hover={{ bg: "rgba(255, 255, 255, 0.7)", transform: "translateY(-2px)" }}
                  cursor="pointer"
                  transition="all 0.3s"
                  onClick={onOpen}
                >
                  <CardBody p={4}>
                    <HStack spacing={4}>
                      <Box p={3} bg="brand.100" borderRadius="xl">
                        <Route size={24} color="var(--chakra-colors-brand-600)" />
                      </Box>
                      <VStack align="start" spacing={1} flex={1}>
                        <Text fontWeight="semibold" color="gray.900">
                          Add New Route
                        </Text>
                        <Text fontSize="sm" color="gray.600">
                          Create and manage transportation routes
                        </Text>
                      </VStack>
                      <Box p={2} bg="brand.500" borderRadius="full">
                        <Plus size={16} color="white" />
                      </Box>
                    </HStack>
                  </CardBody>
                </Card>

                <Card
                  bg="rgba(255, 255, 255, 0.5)"
                  border="1px solid"
                  borderColor="rgba(255, 255, 255, 0.2)"
                  _hover={{ bg: "rgba(255, 255, 255, 0.7)", transform: "translateY(-2px)" }}
                  cursor="pointer"
                  transition="all 0.3s"
                >
                  <CardBody p={4}>
                    <HStack spacing={4}>
                      <Box p={3} bg="green.100" borderRadius="xl">
                        <UserCheck size={24} color="var(--chakra-colors-green-600)" />
                      </Box>
                      <VStack align="start" spacing={1} flex={1}>
                        <Text fontWeight="semibold" color="gray.900">
                          Manage Drivers
                        </Text>
                        <Text fontSize="sm" color="gray.600">
                          Register and approve driver accounts
                        </Text>
                      </VStack>
                      <Box p={2} bg="green.500" borderRadius="full">
                        <Plus size={16} color="white" />
                      </Box>
                    </HStack>
                  </CardBody>
                </Card>

                <Card
                  bg="rgba(255, 255, 255, 0.5)"
                  border="1px solid"
                  borderColor="rgba(255, 255, 255, 0.2)"
                  _hover={{ bg: "rgba(255, 255, 255, 0.7)", transform: "translateY(-2px)" }}
                  cursor="pointer"
                  transition="all 0.3s"
                >
                  <CardBody p={4}>
                    <HStack spacing={4}>
                      <Box p={3} bg="orange.100" borderRadius="xl">
                        <Calendar size={24} color="var(--chakra-colors-orange-600)" />
                      </Box>
                      <VStack align="start" spacing={1} flex={1}>
                        <Text fontWeight="semibold" color="gray.900">
                          View Bookings
                        </Text>
                        <Text fontSize="sm" color="gray.600">
                          Monitor and manage passenger bookings
                        </Text>
                      </VStack>
                      <Box p={2} bg="orange.500" borderRadius="full">
                        <Eye size={16} color="white" />
                      </Box>
                    </HStack>
                  </CardBody>
                </Card>
              </SimpleGrid>
            </CardBody>
          </Card>
        </SimpleGrid>
      </VStack>

      {/* Route Form Modal */}
      <RouteFormModal isOpen={isOpen} onClose={onClose} />
      
      {/* Route View Modal */}
      <RouteViewModal isOpen={isViewOpen} onClose={onViewClose} route={selectedRoute} />
      
      {/* Route Edit Modal */}
      <RouteEditModal isOpen={isEditOpen} onClose={onEditClose} route={selectedRoute} />

      {/* Broadcast Modal */}
      <Modal isOpen={isBroadcastOpen} onClose={onBroadcastClose} size="lg">
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(10px)" />
        <ModalContent borderRadius="2xl">
          <ModalHeader>Send Push Notification</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Target Audience</FormLabel>
                <Select value={broadcastRole} onChange={(e) => setBroadcastRole(e.target.value)}>
                  <option value="all_users">All Users</option>
                  <option value="passenger">Passengers Only</option>
                  <option value="driver">Drivers Only</option>
                </Select>
              </FormControl>
              
              <FormControl isRequired>
                <FormLabel>Notification Title</FormLabel>
                <Input 
                  placeholder="e.g. System Update" 
                  value={broadcastTitle} 
                  onChange={(e) => setBroadcastTitle(e.target.value)} 
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Message Content</FormLabel>
                <Textarea 
                  placeholder="Type your message here..." 
                  rows={4} 
                  value={broadcastBody} 
                  onChange={(e) => setBroadcastBody(e.target.value)} 
                />
              </FormControl>

              <Button 
                colorScheme="brand" 
                w="full" 
                mt={4} 
                isLoading={isBroadcasting} 
                loadingText="Sending..."
                onClick={handleSendBroadcast}
                leftIcon={<Send size={16} />}
              >
                Broadcast Now
              </Button>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  )
}
